import type { QueryFunction } from '@tanstack/react-query';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppState } from '../../AppStateProvider';
import type { Company } from '../../domain/company';
import { appStore } from '../../services/appStore';
import { useDispatchTrackEventWithLocation } from '../../services/tracking/hooks/useDispatchTrackEventWithLocation';

import { companyApi } from './api';
import companyQueryKey from './companyQueryKey';
import { updateCompanyQueryData } from './updateQueryData';

export const useCompanyQuery = () => {
  const queryClient = useQueryClient();
  const { isOpen, companyLinkedinId, companyDomain } = useAppState();

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const handleQuerySuccess = (company?: Company) => {
    if (!company) {
      return;
    }

    updateCompanyQueryData({ companyLinkedinId, company, queryClient });
  };

  const fetchCompanyInfo: QueryFunction<Company, (string | undefined)[]> = async ({
    queryKey: [, linkedinId, domain],
    signal,
  }) => {
    const { data, status } = await companyApi.getCompanyInfo({ linkedinId, domain }, signal);
    if (status === 206) {
      dispatchTrackEventWithLocation('usage limits', 'limit reached', 'overviews');

      appStore.setCompanyQuotaReached(true);
    }
    if (data) {
      handleQuerySuccess(data);
    }
    return data;
  };

  return useQuery({
    queryKey: companyQueryKey.company({ companyLinkedinId, domain: companyDomain }),
    queryFn: fetchCompanyInfo,
    enabled: isOpen && (!!companyLinkedinId || !!companyDomain),
    gcTime: Infinity,
    placeholderData: keepPreviousData,
  });
};
